import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  successionApi,
  employeeApi,
  departmentApi,
  jobBoardApi,
} from '../../services/api';
import {
  PageHeader,
  StatCard,
  EmptyState,
  Spinner,
  Modal,
  ConfirmModal,
} from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  UserPlusIcon,
  UsersIcon,
  ClockIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  ArchiveBoxIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightCircleIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline';

const LEADER_ROLES = ['manager', 'director', 'vp'];

const READINESS = {
  ready_now:          { label: 'Ready now',          chip: 'bg-green-100 text-green-800 border-green-200',   dot: 'bg-green-500'  },
  ready_1_2_years:    { label: 'Ready 1-2 yrs',      chip: 'bg-blue-100  text-blue-800  border-blue-200',   dot: 'bg-blue-500'   },
  needs_development:  { label: 'Needs development',  chip: 'bg-amber-100 text-amber-800 border-amber-200',  dot: 'bg-amber-500'  },
};

const POSTING_STATUS = {
  draft:  'bg-gray-100 text-gray-700 border-gray-200',
  open:   'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-red-100 text-red-800 border-red-200',
  filled: 'bg-blue-100 text-blue-800 border-blue-200',
};

const APP_STATUS = {
  submitted:    'bg-gray-100 text-gray-700 border-gray-200',
  under_review: 'bg-amber-100 text-amber-800 border-amber-200',
  interviewing: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  offered:      'bg-green-100 text-green-800 border-green-200',
  rejected:     'bg-red-100 text-red-800 border-red-200',
  withdrawn:    'bg-gray-100 text-gray-500 border-gray-200',
};

function ReadinessChip({ value }) {
  const r = READINESS[value] || READINESS.needs_development;
  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border', r.chip)}>
      <span className={clsx('w-1.5 h-1.5 rounded-full', r.dot)} />
      {r.label}
    </span>
  );
}

function Avatar({ name, size = 'md' }) {
  const sz = size === 'lg' ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs';
  return (
    <div className={clsx('bg-gradient-to-br from-indigo-100 to-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold shrink-0', sz)}>
      {name?.[0] || '?'}
    </div>
  );
}

function StatusChip({ value, map }) {
  const c = map[value] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize', c)}>
      {value?.replace('_', ' ')}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Add/Edit plan form
// ─────────────────────────────────────────────────────────────
function PlanForm({ leader, initial, employees, existingSuccessorIds = [], onCancel, onSubmit, saving }) {
  const [successorId, setSuccessorId] = useState(initial?.successor_employee_id || '');
  const [readiness, setReadiness]     = useState(initial?.readiness || 'ready_1_2_years');
  const [rank, setRank]               = useState(initial?.rank || 1);
  const [notes, setNotes]             = useState(initial?.notes || '');
  const [query, setQuery]             = useState('');

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return employees
      .filter(e => e.id !== leader?.id)
      .filter(e => !existingSuccessorIds.includes(e.id) || e.id === initial?.successor_employee_id)
      .filter(e => !q || e.name?.toLowerCase().includes(q) || e.role?.toLowerCase().includes(q))
      .slice(0, 50);
  }, [employees, query, leader, existingSuccessorIds, initial]);

  const selected = employees.find(e => e.id === successorId);
  const valid = !!successorId && !!readiness && rank >= 1 && rank <= 3;

  return (
    <div className="space-y-4">
      {leader && (
        <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
          <Avatar name={leader.name} size="lg" />
          <div>
            <p className="text-sm font-semibold text-gray-900">{leader.name}</p>
            <p className="text-xs text-gray-500 capitalize">{leader.role?.replace('_',' ')} · {leader.band}</p>
          </div>
        </div>
      )}

      {!initial && (
        <div>
          <label className="label">Successor</label>
          <div className="relative mb-2">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-9" placeholder="Search employees…" value={query} onChange={e => setQuery(e.target.value)} />
          </div>
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {candidates.length === 0 ? (
              <p className="p-4 text-xs text-gray-400 text-center">No matches</p>
            ) : candidates.map(c => (
              <button key={c.id} type="button" onClick={() => setSuccessorId(c.id)}
                className={clsx(
                  'w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors',
                  successorId === c.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                )}>
                <Avatar name={c.name} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 capitalize truncate">{c.role?.replace('_',' ')} · {c.band}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selected && initial && (
        <div>
          <label className="label">Successor</label>
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
            <Avatar name={selected.name} />
            <div>
              <p className="text-sm font-medium text-gray-900">{selected.name}</p>
              <p className="text-xs text-gray-500 capitalize">{selected.role?.replace('_',' ')} · {selected.band}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="label">Readiness</label>
        <div className="flex flex-col gap-2">
          {Object.entries(READINESS).map(([k, v]) => (
            <label key={k} className={clsx(
              'flex items-center gap-3 px-3 py-2 border rounded-lg cursor-pointer transition-colors',
              readiness === k ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
            )}>
              <input type="radio" name="readiness" value={k} checked={readiness === k} onChange={e => setReadiness(e.target.value)} className="accent-blue-600" />
              <ReadinessChip value={k} />
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Rank (1 = primary successor)</label>
        <div className="flex gap-2">
          {[1, 2, 3].map(n => (
            <button key={n} type="button" onClick={() => setRank(n)}
              className={clsx(
                'px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                rank === n ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              )}>
              #{n}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input h-20 resize-none" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Development gaps, stretch assignments, coaching plan…" />
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button disabled={!valid || saving} className="btn-primary disabled:opacity-40"
          onClick={() => onSubmit({ successor_employee_id: successorId, readiness, rank, notes })}>
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Add successor'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Leader card (with Skill Matches button)
// ─────────────────────────────────────────────────────────────
function LeaderCard({ leader, plans, onAdd, onEdit, onArchive, onSkillMatches }) {
  const sorted = [...plans].sort((a, b) => (a.rank || 99) - (b.rank || 99));
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Avatar name={leader.name} size="lg" />
          <div>
            <p className="font-semibold text-gray-900">{leader.name}</p>
            <p className="text-xs text-gray-500 capitalize">{leader.role?.replace('_',' ')} · {leader.band}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onSkillMatches(leader)}
            title="Skill matches"
            className="btn-sm btn-secondary inline-flex items-center gap-1"
          >
            <SparklesIcon className="w-3.5 h-3.5" />Matches
          </button>
          <button onClick={() => onAdd(leader)} className="btn-sm btn-secondary inline-flex items-center gap-1">
            <PlusIcon className="w-3.5 h-3.5" />Add
          </button>
        </div>
      </div>
      {sorted.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-3 text-center border border-dashed border-gray-200 rounded-lg">
          No successors identified yet
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map(p => (
            <div key={p.id} className={clsx('flex items-center gap-3 p-2 rounded-lg border', p.is_active === false ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-100')}>
              <div className="w-6 text-center text-xs font-semibold text-gray-400">#{p.rank}</div>
              <Avatar name={p.successorEmployee?.name} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{p.successorEmployee?.name || '—'}</p>
                <p className="text-xs text-gray-500 capitalize truncate">{p.successorEmployee?.role?.replace('_',' ')}</p>
              </div>
              <ReadinessChip value={p.readiness} />
              <div className="flex items-center gap-1">
                <button onClick={() => onEdit(p, leader)} title="Edit" className="p-1 rounded hover:bg-gray-100 text-gray-500">
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button onClick={() => onArchive(p)} title={p.is_active === false ? 'Restore' : 'Archive'} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                  <ArchiveBoxIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Skill matches modal
// ─────────────────────────────────────────────────────────────
function SkillMatchesModal({ open, leader, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['succession-skill-matches', leader?.id],
    queryFn:  () => successionApi.skillMatches(leader.id).then(r => r.data),
    enabled:  !!leader,
  });

  return (
    <Modal open={open} onClose={onClose} title={leader ? `Skill matches for ${leader.name}` : 'Skill matches'} size="lg">
      {!leader ? null : isLoading ? (
        <div className="flex justify-center p-10"><Spinner size="md" /></div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">Leader skills</p>
            {data?.leader_skills?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {data.leader_skills.map(s => (
                  <span key={s} className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 capitalize">{s}</span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No skills recorded for this leader yet.</p>
            )}
          </div>

          {!data?.matches?.length ? (
            <EmptyState title="No matches" description="No employees share skills with this leader yet." />
          ) : (
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto -mx-2">
              {data.matches.map(m => (
                <div key={m.employee_id} className="flex items-start gap-3 px-2 py-3">
                  <Avatar name={m.name} size="lg" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                    <p className="text-xs text-gray-500 truncate capitalize">
                      {m.job_title || m.role?.replace('_', ' ')} · {m.band} · {m.department || 'No dept'}
                    </p>
                    {m.matched_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {m.matched_skills.slice(0, 10).map(s => (
                          <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-green-50 text-green-700 border border-green-100 capitalize">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-blue-600">{m.overlap_pct}%</p>
                    <p className="text-[10px] text-gray-400 uppercase">overlap</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Pipeline tab
// ─────────────────────────────────────────────────────────────
function PipelineTab() {
  const qc = useQueryClient();

  const [adding, setAdding]       = useState(null);
  const [editing, setEditing]     = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [matchesLeader, setMatchesLeader] = useState(null);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['succession'],
    queryFn:  () => successionApi.list().then(r => r.data),
  });
  const { data: employees = [], isLoading: empLoading } = useQuery({
    queryKey: ['employees'],
    queryFn:  () => employeeApi.list().then(r => r.data),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn:  () => departmentApi.list().then(r => r.data),
  });

  const createMut = useMutation({
    mutationFn: data => successionApi.create(data),
    onSuccess:  () => { toast.success('Successor added'); qc.invalidateQueries({ queryKey: ['succession'] }); setAdding(null); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to add successor'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => successionApi.update(id, data),
    onSuccess:  () => { toast.success('Plan updated'); qc.invalidateQueries({ queryKey: ['succession'] }); setEditing(null); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to update'),
  });
  const archiveMut = useMutation({
    mutationFn: ({ id, is_active }) => successionApi.update(id, { is_active }),
    onSuccess:  (_, v) => {
      toast.success(v.is_active ? 'Plan restored' : 'Plan archived');
      qc.invalidateQueries({ queryKey: ['succession'] });
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  });

  const activePlans = plans.filter(p => p.is_active !== false);
  const grouped = useMemo(() => {
    const map = new Map();
    plans.forEach(p => {
      if (!map.has(p.role_employee_id)) map.set(p.role_employee_id, { leader: p.roleEmployee, plans: [] });
      map.get(p.role_employee_id).plans.push(p);
    });
    return Array.from(map.values()).filter(g => g.leader);
  }, [plans]);

  const leaders = useMemo(
    () => employees.filter(e => LEADER_ROLES.includes((e.role || '').toLowerCase())),
    [employees]
  );

  const leadersWithPlan = useMemo(() => {
    const s = new Set();
    activePlans.forEach(p => s.add(p.role_employee_id));
    return s;
  }, [activePlans]);

  const criticalRoles = useMemo(
    () => leaders.filter(l => !leadersWithPlan.has(l.id)),
    [leaders, leadersWithPlan]
  );

  const readyNow = activePlans.filter(p => p.readiness === 'ready_now').length;
  const ready12  = activePlans.filter(p => p.readiness === 'ready_1_2_years').length;
  const needsDev = activePlans.filter(p => p.readiness === 'needs_development').length;

  const benchByDept = useMemo(() => {
    return departments.map(d => {
      const deptLeaders = leaders.filter(l => l.department_id === d.id);
      const covered     = deptLeaders.filter(l => leadersWithPlan.has(l.id)).length;
      return {
        id:     d.id,
        name:   d.name,
        total:  deptLeaders.length,
        covered,
        pct:    deptLeaders.length ? Math.round((covered / deptLeaders.length) * 100) : 0,
      };
    }).filter(d => d.total > 0);
  }, [departments, leaders, leadersWithPlan]);

  const allLeaderCards = useMemo(() => {
    const map = new Map();
    grouped.forEach(g => map.set(g.leader.id, g));
    leaders.forEach(l => {
      if (!map.has(l.id)) map.set(l.id, { leader: l, plans: [] });
    });
    return Array.from(map.values());
  }, [grouped, leaders]);

  const activeLeaderPlans = leaderId =>
    activePlans.filter(p => p.role_employee_id === leaderId);

  if (plansLoading || empLoading) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setAdding('any')} className="btn-primary">
          <UserPlusIcon className="w-4 h-4" />New plan
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Ready now"          value={readyNow}            color="green" icon={AcademicCapIcon} />
        <StatCard title="Ready 1-2 yrs"      value={ready12}             color="blue"  icon={ClockIcon} />
        <StatCard title="Needs development"  value={needsDev}            color="amber" icon={UsersIcon} />
        <StatCard title="Uncovered critical" value={criticalRoles.length} color="red"  icon={ExclamationTriangleIcon} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="section-title">Pipeline</h2>
            <span className="text-xs text-gray-500">{allLeaderCards.length} leaders</span>
          </div>

          {allLeaderCards.length === 0 ? (
            <EmptyState
              title="No leaders defined"
              description="Assign manager/director/VP roles to employees to start building a succession pipeline."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allLeaderCards.map(g => (
                <LeaderCard
                  key={g.leader.id}
                  leader={g.leader}
                  plans={g.plans}
                  onAdd={l => setAdding(l)}
                  onEdit={(plan, leader) => setEditing({ plan, leader })}
                  onArchive={plan => setArchiving(plan)}
                  onSkillMatches={l => setMatchesLeader(l)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="section-title mb-3">Critical roles · no successor</h2>
            <div className="card p-4">
              {criticalRoles.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">All critical roles covered.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {criticalRoles.map(l => (
                    <li key={l.id} className="py-2 flex items-center gap-3 first:pt-0 last:pb-0">
                      <Avatar name={l.name} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{l.name}</p>
                        <p className="text-xs text-gray-500 capitalize truncate">{l.role?.replace('_',' ')}</p>
                      </div>
                      <button onClick={() => setAdding(l)} className="btn-sm btn-secondary">Add</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <h2 className="section-title mb-3">Bench strength by department</h2>
            <div className="card p-4 space-y-3">
              {benchByDept.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">No departments with leaders.</p>
              ) : benchByDept.map(d => (
                <div key={d.id}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700 truncate">{d.name}</span>
                    <span className="text-gray-500 font-mono">{d.covered}/{d.total} · {d.pct}%</span>
                  </div>
                  <div className="bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className={clsx(
                      'h-full transition-all',
                      d.pct >= 75 ? 'bg-green-500' : d.pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
                    )} style={{ width: `${d.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={!!adding}
        onClose={() => setAdding(null)}
        title={adding && adding !== 'any'
          ? `Add successor for ${adding.name}`
          : 'Add succession plan'}
        size="md"
      >
        {adding && (
          adding === 'any' ? (
            <LeaderPickerThenForm
              leaders={leaders}
              employees={employees}
              existingFor={leaderId => activeLeaderPlans(leaderId).map(p => p.successor_employee_id)}
              onCancel={() => setAdding(null)}
              onSubmit={(leaderId, data) => createMut.mutate({ role_employee_id: leaderId, ...data })}
              saving={createMut.isPending}
            />
          ) : (
            <PlanForm
              leader={adding}
              employees={employees}
              existingSuccessorIds={activeLeaderPlans(adding.id).map(p => p.successor_employee_id)}
              onCancel={() => setAdding(null)}
              onSubmit={data => createMut.mutate({ role_employee_id: adding.id, ...data })}
              saving={createMut.isPending}
            />
          )
        )}
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit succession plan" size="md">
        {editing && (
          <PlanForm
            leader={editing.leader}
            initial={editing.plan}
            employees={employees}
            onCancel={() => setEditing(null)}
            onSubmit={data => updateMut.mutate({ id: editing.plan.id, data })}
            saving={updateMut.isPending}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!archiving}
        onClose={() => setArchiving(null)}
        onConfirm={() => archiving && archiveMut.mutate({ id: archiving.id, is_active: archiving.is_active === false })}
        title={archiving?.is_active === false ? 'Restore plan?' : 'Archive plan?'}
        message={archiving?.is_active === false
          ? 'Reactivate this succession plan for ongoing tracking.'
          : 'This plan will be hidden from active pipeline but kept for audit history.'}
        danger={archiving?.is_active !== false}
      />

      <SkillMatchesModal
        open={!!matchesLeader}
        leader={matchesLeader}
        onClose={() => setMatchesLeader(null)}
      />
    </div>
  );
}

function LeaderPickerThenForm({ leaders, employees, existingFor, onCancel, onSubmit, saving }) {
  const [leaderId, setLeaderId] = useState('');
  const [q, setQ]               = useState('');

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return leaders.filter(l => !s || l.name?.toLowerCase().includes(s) || l.role?.toLowerCase().includes(s)).slice(0, 50);
  }, [leaders, q]);

  const leader = leaders.find(l => l.id === leaderId);
  if (leader) {
    return (
      <PlanForm
        leader={leader}
        employees={employees}
        existingSuccessorIds={existingFor(leader.id)}
        onCancel={onCancel}
        onSubmit={data => onSubmit(leader.id, data)}
        saving={saving}
      />
    );
  }

  return (
    <div className="space-y-3">
      <label className="label">Select leader</label>
      <div className="relative">
        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input className="input pl-9" placeholder="Search leaders…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {filtered.length === 0 ? (
          <p className="p-4 text-xs text-gray-400 text-center">No leaders match</p>
        ) : filtered.map(l => (
          <button key={l.id} type="button" onClick={() => setLeaderId(l.id)}
            className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-gray-50">
            <Avatar name={l.name} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{l.name}</p>
              <p className="text-xs text-gray-500 capitalize truncate">{l.role?.replace('_',' ')} · {l.band}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Bench Strength tab
// ─────────────────────────────────────────────────────────────
function urgencyColor(score) {
  if (score >= 7) return 'bg-red-50   text-red-700   border-red-200';
  if (score >= 4) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-green-50 text-green-700 border-green-200';
}

function BenchStrengthTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['succession-bench-strength'],
    queryFn:  () => successionApi.benchStrength().then(r => r.data),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (error) return <EmptyState title="Unable to load report" description="Please try again later." />;
  if (!data || data.total_critical_roles === 0) {
    return (
      <EmptyState
        title="No critical roles yet"
        description="Mark employees as holding critical roles or assign manager/director/VP roles to see bench strength analytics."
      />
    );
  }

  const pct = data.coverage_pct || 0;
  const ringStyle = {
    background: `conic-gradient(#2563eb ${pct * 3.6}deg, #e5e7eb 0deg)`,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Overall gauge */}
        <div className="card p-6 flex flex-col items-center justify-center">
          <div className="relative w-40 h-40 rounded-full flex items-center justify-center" style={ringStyle}>
            <div className="w-32 h-32 bg-white rounded-full flex flex-col items-center justify-center">
              <p className="text-4xl font-bold text-gray-900">{pct}%</p>
              <p className="text-xs text-gray-500 uppercase tracking-wide">coverage</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600 text-center">
            <span className="font-semibold text-gray-900">{data.covered_critical_roles}</span> of{' '}
            <span className="font-semibold text-gray-900">{data.total_critical_roles}</span> critical roles
            have at least one ready successor.
          </p>
        </div>

        {/* Dept grid */}
        <div className="card p-6 md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <ChartPieIcon className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Coverage by department</h3>
          </div>
          {data.by_department.length === 0 ? (
            <p className="text-sm text-gray-500">No departmental data.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.by_department.map(d => (
                <div key={d.department_id ?? d.department} className="p-3 border border-gray-100 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-800 truncate">{d.department}</p>
                    <span className="text-xs text-gray-500 font-mono">{d.covered}/{d.total}</span>
                  </div>
                  <div className="bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full transition-all',
                        d.coverage_pct >= 75 ? 'bg-green-500' : d.coverage_pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
                      )}
                      style={{ width: `${d.coverage_pct}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">{d.coverage_pct}% covered</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Risk × Criticality matrix */}
      <div className="card p-0 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Risk × Criticality matrix</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Urgency score combines attrition risk (from latest rating) with role criticality. Higher scores demand attention first.
          </p>
        </div>
        {data.risk_matrix.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">No critical roles to evaluate.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">Employee</th>
                  <th className="th">Role · Dept</th>
                  <th className="th">Successors</th>
                  <th className="th">Ready</th>
                  <th className="th">Risk</th>
                  <th className="th">Critical</th>
                  <th className="th">Urgency</th>
                </tr>
              </thead>
              <tbody>
                {data.risk_matrix.map(r => (
                  <tr key={r.employee_id} className="tr-hover">
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <Avatar name={r.employee_name} />
                        <span className="text-sm font-medium text-gray-900">{r.employee_name}</span>
                      </div>
                    </td>
                    <td className="td">
                      <p className="text-sm capitalize">{r.role?.replace('_', ' ')}</p>
                      <p className="text-xs text-gray-500">{r.department}</p>
                    </td>
                    <td className="td text-sm">{r.successors}</td>
                    <td className="td text-sm">{r.ready_successors}</td>
                    <td className="td">
                      <span className={clsx(
                        'inline-flex px-2 py-0.5 rounded text-xs font-medium border capitalize',
                        r.attrition_risk === 'high'   && 'bg-red-50 text-red-700 border-red-200',
                        r.attrition_risk === 'medium' && 'bg-amber-50 text-amber-700 border-amber-200',
                        r.attrition_risk === 'low'    && 'bg-green-50 text-green-700 border-green-200',
                      )}>
                        {r.attrition_risk}
                      </span>
                    </td>
                    <td className="td">
                      {r.is_critical ? (
                        <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="td">
                      <span className={clsx(
                        'inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded text-xs font-semibold border',
                        urgencyColor(r.urgency_score)
                      )}>
                        {r.urgency_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Job Board (HR) tab
// ─────────────────────────────────────────────────────────────
function TagsInput({ value = [], onChange, placeholder }) {
  const [text, setText] = useState('');
  const commit = () => {
    const t = text.trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setText('');
  };
  return (
    <div className="border border-gray-200 rounded-lg p-2 focus-within:border-blue-500 transition-colors">
      <div className="flex flex-wrap gap-1 mb-1">
        {value.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-100">
            {t}
            <button type="button" onClick={() => onChange(value.filter(v => v !== t))} className="hover:text-red-600">
              <XCircleIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        className="w-full text-sm outline-none bg-transparent"
        placeholder={placeholder || 'Type and press Enter'}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && !text && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={commit}
      />
    </div>
  );
}

function PostingForm({ initial, departments, onCancel, onSubmit, saving }) {
  const [title, setTitle]           = useState(initial?.title || '');
  const [deptId, setDeptId]         = useState(initial?.department_id || '');
  const [role, setRole]             = useState(initial?.required_role || '');
  const [band, setBand]             = useState(initial?.required_band || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [required, setRequired]     = useState(initial?.required_skills || []);
  const [preferred, setPreferred]   = useState(initial?.preferred_skills || []);
  const [location, setLocation]     = useState(initial?.location || '');
  const [isRemote, setIsRemote]     = useState(!!initial?.is_remote);
  const [closesAt, setClosesAt]     = useState(
    initial?.closes_at ? String(initial.closes_at).slice(0, 10) : ''
  );

  const valid = title.trim().length > 0;

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Title</label>
        <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Engineering Lead" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Department</label>
          <select className="input" value={deptId} onChange={e => setDeptId(e.target.value)}>
            <option value="">—</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Closes on</label>
          <input type="date" className="input" value={closesAt} onChange={e => setClosesAt(e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Required role</label>
          <input className="input" value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. manager" />
        </div>
        <div>
          <label className="label">Required band</label>
          <input className="input" value={band} onChange={e => setBand(e.target.value)} placeholder="e.g. senior" />
        </div>
      </div>
      <div>
        <label className="label">Required skills</label>
        <TagsInput value={required} onChange={setRequired} placeholder="Add a skill, press Enter" />
      </div>
      <div>
        <label className="label">Preferred skills</label>
        <TagsInput value={preferred} onChange={setPreferred} placeholder="Nice-to-haves, press Enter" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Location</label>
          <input className="input" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. NYC" />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" className="accent-blue-600" checked={isRemote} onChange={e => setIsRemote(e.target.checked)} />
            Remote-friendly
          </label>
        </div>
      </div>
      <div>
        <label className="label">Description</label>
        <textarea className="input h-24 resize-none" value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Role summary, responsibilities, growth opportunities…" />
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button
          disabled={!valid || saving}
          onClick={() => onSubmit({
            title,
            department_id: deptId || null,
            required_role: role || null,
            required_band: band || null,
            description: description || null,
            required_skills: required,
            preferred_skills: preferred,
            location: location || null,
            is_remote: isRemote,
            closes_at: closesAt || null,
          })}
          className="btn-primary disabled:opacity-40"
        >
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Create posting'}
        </button>
      </div>
    </div>
  );
}

function ApplicantsModal({ open, posting, onClose, onFill }) {
  const { data: applicants = [], isLoading } = useQuery({
    queryKey: ['posting-applicants', posting?.id],
    queryFn:  () => jobBoardApi.applicants(posting.id).then(r => r.data),
    enabled:  !!posting,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={posting ? `Applicants · ${posting.title}` : 'Applicants'}
    >
      {!posting ? null : isLoading ? (
        <div className="flex justify-center p-10"><Spinner size="md" /></div>
      ) : !applicants.length ? (
        <EmptyState title="No applicants yet" description="Once employees apply, they will appear here ranked by skill match." />
      ) : (
        <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto -mx-2">
          {applicants.map(a => (
            <div key={a.id} className="flex items-start gap-3 px-2 py-3">
              <Avatar name={a.employee?.name} size="lg" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{a.employee?.name}</p>
                <p className="text-xs text-gray-500 truncate capitalize">
                  {a.employee?.role?.replace('_', ' ')} · {a.employee?.band}
                </p>
                {a.matched_skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {a.matched_skills.slice(0, 10).map(s => (
                      <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-green-50 text-green-700 border border-green-100 capitalize">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                {a.cover_letter && (
                  <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded line-clamp-3">{a.cover_letter}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-600">{a.match_score}%</p>
                  <p className="text-[10px] text-gray-400 uppercase">match</p>
                </div>
                <StatusChip value={a.status} map={APP_STATUS} />
                {posting.status === 'open' && (
                  <button
                    onClick={() => onFill(a.employee_id)}
                    className="btn-sm btn-primary inline-flex items-center gap-1"
                  >
                    <ArrowRightCircleIcon className="w-3.5 h-3.5" /> Fill with
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function JobBoardTab() {
  const qc = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [applicantsFor, setApplicantsFor] = useState(null);

  const { data: postings = [], isLoading } = useQuery({
    queryKey: ['job-postings'],
    queryFn:  () => jobBoardApi.list().then(r => r.data),
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn:  () => departmentApi.list().then(r => r.data),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['job-postings'] });
    qc.invalidateQueries({ queryKey: ['posting-applicants'] });
  };

  const createMut = useMutation({
    mutationFn: data => jobBoardApi.create(data),
    onSuccess:  () => { toast.success('Posting created as draft'); invalidate(); setCreating(false); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to create posting'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => jobBoardApi.update(id, data),
    onSuccess:  () => { toast.success('Posting updated'); invalidate(); setEditing(null); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to update posting'),
  });
  const openMut = useMutation({
    mutationFn: id => jobBoardApi.open(id),
    onSuccess:  () => { toast.success('Posting opened'); invalidate(); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to open'),
  });
  const closeMut = useMutation({
    mutationFn: id => jobBoardApi.close(id),
    onSuccess:  () => { toast.success('Posting closed'); invalidate(); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to close'),
  });
  const fillMut = useMutation({
    mutationFn: ({ id, employee_id }) => jobBoardApi.fill(id, { employee_id }),
    onSuccess:  () => { toast.success('Posting marked as filled'); invalidate(); setApplicantsFor(null); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to mark filled'),
  });
  const deleteMut = useMutation({
    mutationFn: id => jobBoardApi.delete(id),
    onSuccess:  () => { toast.success('Posting deleted'); invalidate(); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="section-title">Internal postings</h2>
          <p className="text-xs text-gray-500">{postings.length} total</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">
          <PlusIcon className="w-4 h-4" />New posting
        </button>
      </div>

      {!postings.length ? (
        <EmptyState
          title="No postings yet"
          description="Create an internal posting to advertise open roles to your employees."
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">Title</th>
                  <th className="th">Department</th>
                  <th className="th">Required</th>
                  <th className="th">Status</th>
                  <th className="th">Applicants</th>
                  <th className="th">Closes</th>
                  <th className="th text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {postings.map(p => (
                  <tr key={p.id} className="tr-hover">
                    <td className="td">
                      <p className="text-sm font-medium text-gray-900">{p.title}</p>
                      <p className="text-xs text-gray-500 capitalize">
                        {p.required_role?.replace('_', ' ') || '—'} · {p.required_band || '—'}
                      </p>
                    </td>
                    <td className="td text-sm">{p.department?.name || '—'}</td>
                    <td className="td">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(p.required_skills || []).slice(0, 4).map(s => (
                          <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 text-indigo-700 border border-indigo-100 capitalize">
                            {s}
                          </span>
                        ))}
                        {(p.required_skills || []).length > 4 && (
                          <span className="text-[10px] text-gray-400">+{p.required_skills.length - 4}</span>
                        )}
                      </div>
                    </td>
                    <td className="td"><StatusChip value={p.status} map={POSTING_STATUS} /></td>
                    <td className="td text-sm">{p.applications_count ?? 0}</td>
                    <td className="td text-xs text-gray-500">
                      {p.closes_at ? String(p.closes_at).slice(0, 10) : '—'}
                    </td>
                    <td className="td text-right pr-2">
                      <div className="inline-flex items-center gap-1">
                        {p.status === 'draft' && (
                          <button
                            onClick={() => openMut.mutate(p.id)}
                            disabled={openMut.isPending}
                            className="btn-sm btn-primary"
                          >
                            Open
                          </button>
                        )}
                        {p.status === 'open' && (
                          <button
                            onClick={() => closeMut.mutate(p.id)}
                            disabled={closeMut.isPending}
                            className="btn-sm btn-secondary"
                          >
                            Close
                          </button>
                        )}
                        <button
                          onClick={() => setApplicantsFor(p)}
                          className="btn-sm btn-secondary"
                        >
                          Applicants
                        </button>
                        <button
                          onClick={() => setEditing(p)}
                          title="Edit"
                          className="p-1 rounded hover:bg-gray-100 text-gray-500"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleting(p)}
                          title="Delete"
                          className="p-1 rounded hover:bg-gray-100 text-gray-500"
                        >
                          <XCircleIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New job posting" size="lg">
        <PostingForm
          departments={departments}
          onCancel={() => setCreating(false)}
          onSubmit={data => createMut.mutate(data)}
          saving={createMut.isPending}
        />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit posting" size="lg">
        {editing && (
          <PostingForm
            initial={editing}
            departments={departments}
            onCancel={() => setEditing(null)}
            onSubmit={data => updateMut.mutate({ id: editing.id, data })}
            saving={updateMut.isPending}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
        title="Delete posting?"
        message="This will permanently remove the posting and any applications tied to it."
        danger
      />

      <ApplicantsModal
        open={!!applicantsFor}
        posting={applicantsFor}
        onClose={() => setApplicantsFor(null)}
        onFill={(empId) => applicantsFor && fillMut.mutate({ id: applicantsFor.id, employee_id: empId })}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// My Opportunities tab
// ─────────────────────────────────────────────────────────────
function MyOpportunitiesTab() {
  const qc = useQueryClient();
  const [applyFor, setApplyFor] = useState(null);
  const [cover, setCover]       = useState('');

  const { data: postings = [], isLoading } = useQuery({
    queryKey: ['my-job-matches'],
    queryFn:  () => jobBoardApi.myMatches().then(r => r.data),
  });

  const applyMut = useMutation({
    mutationFn: ({ id, cover_letter }) => jobBoardApi.apply(id, { cover_letter }),
    onSuccess:  () => {
      toast.success('Application submitted');
      qc.invalidateQueries({ queryKey: ['my-job-matches'] });
      setApplyFor(null);
      setCover('');
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to apply'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!postings.length) {
    return (
      <EmptyState
        title="No open postings"
        description="There are no open internal postings right now. Check back later."
      />
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {postings.map(p => (
          <div key={p.id} className="card p-5">
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{p.title}</p>
                <p className="text-xs text-gray-500">
                  {p.department?.name || '—'} · {p.location || (p.is_remote ? 'Remote' : '—')}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-2xl font-bold text-blue-600">{p.match_pct}%</p>
                <p className="text-[10px] text-gray-400 uppercase">match</p>
              </div>
            </div>

            {p.matched_skills?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {p.matched_skills.slice(0, 8).map(s => (
                  <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-green-50 text-green-700 border border-green-100 capitalize">
                    {s}
                  </span>
                ))}
              </div>
            )}

            {p.description && (
              <p className="text-sm text-gray-600 line-clamp-3 mb-3">{p.description}</p>
            )}

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {p.closes_at ? `Closes ${String(p.closes_at).slice(0, 10)}` : 'No close date'}
              </span>
              {p.application_status ? (
                <StatusChip value={p.application_status} map={APP_STATUS} />
              ) : (
                <button
                  className="btn-sm btn-primary"
                  onClick={() => { setApplyFor(p); setCover(''); }}
                >
                  Apply
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!applyFor} onClose={() => setApplyFor(null)} title={applyFor ? `Apply · ${applyFor.title}` : 'Apply'} size="md">
        {applyFor && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">{applyFor.title}</p>
              <p className="text-xs text-gray-500">{applyFor.department?.name || '—'}</p>
              {applyFor.matched_skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {applyFor.matched_skills.map(s => (
                    <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-green-50 text-green-700 border border-green-100 capitalize">
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="label">Cover letter (optional)</label>
              <textarea
                className="input h-32 resize-none"
                value={cover}
                onChange={e => setCover(e.target.value)}
                placeholder="Why are you a good fit? What would you bring to the role?"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setApplyFor(null)} className="btn-secondary">Cancel</button>
              <button
                disabled={applyMut.isPending}
                onClick={() => applyMut.mutate({ id: applyFor.id, cover_letter: cover })}
                className="btn-primary disabled:opacity-40"
              >
                {applyMut.isPending ? 'Submitting…' : 'Submit application'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'pipeline', label: 'Pipeline',          icon: UsersIcon },
  { id: 'bench',    label: 'Bench Strength',    icon: ChartPieIcon },
  { id: 'jobs',     label: 'Job Board',         icon: BriefcaseIcon },
  { id: 'my',       label: 'My Opportunities',  icon: SparklesIcon },
];

export default function Succession() {
  const [tab, setTab] = useState('pipeline');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Succession Planning"
        subtitle="Identify and develop successors for critical leadership roles."
      />

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 -mb-px">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={clsx(
                  'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  active
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === 'pipeline' && <PipelineTab />}
      {tab === 'bench'    && <BenchStrengthTab />}
      {tab === 'jobs'     && <JobBoardTab />}
      {tab === 'my'       && <MyOpportunitiesTab />}
    </div>
  );
}
